import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useApiClient } from "@/lib/auth";
import { useAppStore } from "@/store";
import {
  useFormSchedule,
  useUpdateFormSchedule,
  type FormSchedule,
} from "@/hooks/use-form-schedules";
import { useFormTemplate } from "@/hooks/use-form-template";
import { useToast } from "@/hooks/use-toast";
import {
  coherenceErrorStep,
  coherenceErrorToken,
  mapCoherenceError,
  noCoherenceErrors,
  type CoherenceErrors,
} from "@/components/form-schedules/coherence-errors";
import {
  TAB_FOR_COHERENCE_STEP,
  buildSchedulePatch,
  initialRecurrenceEditState,
  parseEditTab,
  rebuiltRecurrenceEditState,
  scheduleEditIsDirty,
  type RecurrenceEditState,
  type ScheduleEditTab,
} from "@/components/form-schedules/edit-state";
import { RecurrenceBuilder } from "@/components/form-schedules/wizard/recurrence-builder";
import { FacilityBulkPicker } from "@/components/form-schedules/wizard/facility-bulk-picker";
import { materializeMatchingIds } from "@/components/form-schedules/wizard/materialize-matching-ids";
import {
  Breadcrumbs,
  Button,
  Card,
  FormField,
  PageHeader,
  PageSkeleton,
  Tabs,
  inputClass,
  type TabDef,
} from "@/components/ui";

const EDIT = "schedules.wizard.edit";

interface ScheduleEditSearch {
  tab?: ScheduleEditTab;
}

export const Route = createFileRoute("/_authenticated/test-program/schedules/$id/edit")({
  component: FormScheduleEditPage,
  validateSearch: (search: Record<string, unknown>): ScheduleEditSearch => ({
    tab: parseEditTab(search.tab),
  }),
});

/**
 * Tabbed edit page (Basics | Recurrence | Facilities) reusing the creation
 * wizard's components — deep-linkable via `?tab=`, one sticky Save/Cancel
 * footer, one PATCH. `template_id` is immutable provenance (absent from the
 * Update schema — compile-time enforced); the body carries only dirty fields
 * per `buildSchedulePatch`. A stored UNTIL rule rehydrates as Ends=On and any
 * save emits the normalized form (`end_date` set, UNTIL dropped); rules
 * outside the builder grammar render the read-only unsupported-rule mode with
 * a Rebuild action.
 */
function FormScheduleEditPage() {
  const { t } = useTranslation(["test-cadence", "common", "nav"]);
  const { id } = Route.useParams();
  const orgId = useAppStore((s) => s.activeOrgId);
  const { data: schedule, isLoading } = useFormSchedule(id);
  const { data: template } = useFormTemplate(schedule?.template_id);

  if (isLoading || !schedule || !orgId) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <PageSkeleton />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Breadcrumbs
        items={[
          {
            label: t("testProgram.schedules", { ns: "nav" }),
            to: "/test-program",
            search: { tab: "schedules" },
          },
          { label: schedule.name },
        ]}
      />
      <PageHeader title={t(`${EDIT}.title`)} subtitle={schedule.name} />

      <div className="mt-6">
        <ScheduleEditTabs
          key={schedule.id}
          orgId={orgId}
          schedule={schedule}
          templateTitle={template?.title}
        />
      </div>
    </div>
  );
}

interface ScheduleEditTabsProps {
  orgId: string;
  schedule: FormSchedule;
  /** Resolved title for the immutable template FK (arrives after mount). */
  templateTitle?: string;
}

function ScheduleEditTabs({ orgId, schedule, templateTitle }: ScheduleEditTabsProps) {
  const { t } = useTranslation(["test-cadence", "common"]);
  const navigate = useNavigate();
  const toasts = useToast();
  const api = useApiClient();
  const { tab } = Route.useSearch();
  const activeTab: ScheduleEditTab = tab ?? "basics";

  const [name, setName] = useState(schedule.name);
  const [active, setActive] = useState(schedule.active);
  const [recurrence, setRecurrence] = useState<RecurrenceEditState>(() =>
    initialRecurrenceEditState(schedule),
  );
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(schedule.facility_ids),
  );
  const [labels, setLabels] = useState<ReadonlyMap<string, string>>(new Map());
  const [facilitiesTouched, setFacilitiesTouched] = useState(false);

  const [errors, setErrors] = useState<CoherenceErrors>(noCoherenceErrors);
  const [nameError, setNameError] = useState<string | null>(null);
  const [staleFacilityIds, setStaleFacilityIds] = useState<ReadonlySet<string>>(new Set());
  const updateMutation = useUpdateFormSchedule(schedule.id);

  const draft = { name, active, recurrence, selected, facilitiesTouched };
  const isDirty = scheduleEditIsDirty(schedule, draft);

  const setActiveTab = (next: ScheduleEditTab) => {
    void navigate({
      to: "/test-program/schedules/$id/edit",
      params: { id: schedule.id },
      search: next === "basics" ? {} : { tab: next },
      replace: true,
    });
  };

  /**
   * The invalid_facility 422 envelope carries no ids, so the offenders are
   * derived by diffing the selection against the org's current facility list
   * (the wizard's create path does the same). Best-effort: over-cap orgs and
   * fetch failures fall back to banner-only.
   */
  async function flagStaleFacilities(currentSelection: ReadonlySet<string>) {
    try {
      const result = await materializeMatchingIds(api, orgId, {});
      if ("overCap" in result) return;
      const visible = new Set(result.rows.map((row) => row.id));
      setStaleFacilityIds(new Set([...currentSelection].filter((id) => !visible.has(id))));
    } catch {
      // Banner-only fallback — chip flagging is a best-effort refinement.
    }
  }

  function handleSave() {
    setErrors(noCoherenceErrors);
    setStaleFacilityIds(new Set());
    if (name.trim() === "") {
      setNameError(t("schedules.wizard.basics.nameRequired"));
      setActiveTab("basics");
      return;
    }
    setNameError(null);
    updateMutation.mutate(buildSchedulePatch(schedule, draft), {
      onSuccess: () => {
        toasts.success("test-cadence:schedules.wizard.edit.saveSuccess");
        void navigate({ to: "/test-program", search: { tab: "schedules" } });
      },
      onError: (err) => {
        setErrors(mapCoherenceError(err, t("errors.updateFailed"), t));
        const token = coherenceErrorToken(err);
        if (token) {
          setActiveTab(TAB_FOR_COHERENCE_STEP[coherenceErrorStep[token]]);
          if (token === "invalid_facility") {
            void flagStaleFacilities(selected);
          }
        }
      },
    });
  }

  const tabs: TabDef<ScheduleEditTab>[] = [
    { value: "basics", label: t(`${EDIT}.tabBasics`) },
    { value: "recurrence", label: t(`${EDIT}.tabRecurrence`) },
    { value: "facilities", label: t(`${EDIT}.tabFacilities`), count: selected.size },
  ];

  return (
    <div>
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        ariaLabel={t(`${EDIT}.title`)}
      />

      <div
        className="mt-6"
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {activeTab === "basics" && (
          <Card>
            <div className="space-y-4">
              <FormField
                label={t("schedules.wizard.basics.name")}
                required
                error={nameError ?? undefined}
              >
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                />
              </FormField>

              <FormField
                label={t("schedules.wizard.basics.template")}
                hint={t(`${EDIT}.templateImmutable`)}
                error={errors.templateError ?? undefined}
              >
                {/* The resolved title can arrive after mount (separate template
                    detail query) — read the prop directly, never a mount-time
                    state copy. */}
                <input
                  type="text"
                  value={templateTitle ?? schedule.template_id}
                  disabled
                  readOnly
                  className={inputClass}
                />
              </FormField>

              <div>
                <label className="flex items-center gap-2 text-sm text-on-surface">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                  />
                  {t("schedules.wizard.basics.active")}
                </label>
                <p className="mt-1 text-xs text-muted">{t("schedules.wizard.basics.activeHint")}</p>
              </div>
            </div>
          </Card>
        )}

        {activeTab === "recurrence" && (
          <Card>
            {recurrence.mode === "unsupported" ? (
              <div role="alert" className="space-y-3 rounded-md bg-warning/10 p-4">
                <p className="text-sm font-semibold text-on-surface">
                  {t(`${EDIT}.unsupportedRule`)}
                </p>
                <p className="text-sm text-on-surface">{t(`${EDIT}.unsupportedRuleBody`)}</p>
                <code className="block rounded-md border border-gray-200 bg-gray-100 px-3 py-2 font-mono text-sm text-on-surface dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                  {schedule.rrule}
                </code>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setRecurrence((prev) => rebuiltRecurrenceEditState(schedule, prev))
                  }
                >
                  {t(`${EDIT}.rebuildRule`)}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <RecurrenceBuilder
                  value={recurrence.value}
                  onChange={(next) =>
                    setRecurrence((prev) => ({ ...prev, value: next, touched: true }))
                  }
                />
                {errors.rruleError && (
                  <div role="alert" className="rounded-md bg-danger/10 p-3 text-sm text-danger">
                    {errors.rruleError}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {activeTab === "facilities" && (
          <Card>
            <div className="space-y-4">
              {errors.facilitiesError && (
                <div role="alert" className="rounded-md bg-danger/10 p-3 text-sm text-danger">
                  {errors.facilitiesError}
                </div>
              )}
              <FacilityBulkPicker
                orgId={orgId}
                selected={selected}
                labels={labels}
                emptyWarning={t(`${EDIT}.clearWarning`)}
                flaggedIds={staleFacilityIds}
                onChange={(nextSelected, nextLabels) => {
                  setSelected(nextSelected);
                  setLabels(nextLabels);
                  setFacilitiesTouched(true);
                }}
              />
            </div>
          </Card>
        )}
      </div>

      <div className="sticky bottom-0 z-10 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {errors.submitError ? (
          <div role="alert" className="text-sm text-danger">
            {errors.submitError}
          </div>
        ) : (
          <span />
        )}
        <div className="flex gap-2.5">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate({ to: "/test-program", search: { tab: "schedules" } })}
          >
            {t(`${EDIT}.cancel`)}
          </Button>
          <Button
            type="button"
            disabled={!isDirty || updateMutation.isPending}
            onClick={handleSave}
          >
            {t(`${EDIT}.save`)}
          </Button>
        </div>
      </div>
    </div>
  );
}
